import { useEffect, useState, type ReactNode } from "react";
import { t } from "./lib/i18n";

interface AppBridgeProviderProps {
  apiKey: string;
  host: string | null;
  children: ReactNode;
}

/**
 * App Bridge v4 の初期化待ち。
 *
 * 本体は index.html の CDN `<script>`（🔴 最初のタグでなければ初期化を拒否する）が
 * 読み込み、完了すると `window.shopify` が生える。それを待ってから children を描く
 * ＝`shopify.idToken()` を呼ぶ API クライアントが「まだ居ない」に当たらない。
 *
 * `host` は認証判断に使わない。有効なidTokenを提供できるApp Bridgeを待つ。
 * ローカル開発では `VITE_DEV_SESSION_TOKEN` があれば API クライアントがそれを使う。
 */
export function AppBridgeProvider({ apiKey, children }: AppBridgeProviderProps) {
  const [isReady, setIsReady] = useState(() => typeof window.shopify?.idToken === "function");
  const [timedOut, setTimedOut] = useState(false);
  const shouldSkip = !apiKey;

  useEffect(() => {
    if (shouldSkip) return;
    if (window.shopify?.idToken) {
      setIsReady(true);
      return;
    }
    const interval = setInterval(() => {
      if (window.shopify?.idToken) {
        setIsReady(true);
        clearInterval(interval);
      }
    }, POLL_INTERVAL_MS);
    const timeout = setTimeout(() => {
      clearInterval(interval);
      setTimedOut(true);
    }, POLL_TIMEOUT_MS);
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [shouldSkip]);

  if (shouldSkip) return <>{children}</>;
  if (!isReady) return <p role={timedOut ? "alert" : "status"}>{t(timedOut
    ? "Shopifyの認証を確認できません。Shopify管理画面からアプリを開き直してください。"
    : "Shopifyとの接続を確認しています")}</p>;
  return <>{children}</>;
}

const POLL_INTERVAL_MS = 50;
const POLL_TIMEOUT_MS = 10_000;
